import * as React from "react";
import { useEffect, useState } from "react";
import { useDataProvider } from "react-admin";
import { Grid, Tooltip, Typography, withStyles } from "@material-ui/core";
import { ResponsiveContainer, Cell, Pie, PieChart, Sector } from "recharts";
import GaugeChart from "../../../Components/CustomChart/GaugeChart";

const CustomizedTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: "90%",
    fontSize: theme.typography.pxToRem(14),
    border: "1px solid #dadde9",
  },
}))(Tooltip);

const FailOnFitRateSource = ({ filterParams }) => {
  const dataProvider = useDataProvider();

  const [chartData, setChartData] = useState([] as any);

  const resourceEndpoint = "reliabilityfailonfits/fofratesource";

  useEffect(() => {
    GetChartData();
  }, [filterParams]);

  const GetChartData = async () => {
    await dataProvider
      .getList(resourceEndpoint, {
        pagination: { page: 1, perPage: 99999 },
        sort: { field: "id", order: "ASC" },
        filter: filterParams,
      })
      .then(({ data }) => {
        if (data != null && data.length > 0) setChartData(data[0]);
        else setChartData([]);
      })
      .catch(error => {
        console.log(error);
      });
  };

  const subArcs = [
    {
      limit: 1.5,
      color: "#5BE12C",
      showTick: true,
    },
    {
      limit: 2,
      color: "#FF7A00",
      showTick: true,
    },
    {
      limit: 10,
      color: "#EA4228",
      showTick: true,
    },
  ];

  const COLORS = ["#0088FE", "#FF8042", "#00C49F", "#FFBB28"];

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = props => {
    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill="#999">
          {`${payload.vendorType}: ${(percent * 100).toFixed(2)}%`}
        </text>
      </g>
    );
  };

  return (
    <>
      <Typography gutterBottom variant="h5" component="h2" align="center">
        Fail on Fit Rate / Source
      </Typography>
      <Grid container direction="row" justifyContent="center" alignItems="center">
        <Grid item>
          <div style={{ width: 500, height: 400 }}>
            <GaugeChart
              value={chartData?.confirmedFofByTotalRequestRatio}
              subArcs={subArcs}
              maxValue={10}
              style={{ width: 500, height: 400 }}
            />
          </div>
        </Grid>
        <Grid item>
          <div style={{ width: 700, height: 500 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData?.vendorTypes}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={renderCustomizedLabel}
                  outerRadius={140}
                  fill="#8884d8"
                  dataKey="vendorTypeCount"
                  nameKey="vendorType"
                >
                  {chartData?.vendorTypes?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Grid>
      </Grid>
      <Grid container direction="row" justifyContent="center" alignItems="center">
        <Grid
          container
          xs={8}
          direction="row"
          justifyContent="center"
          alignItems="center"
          style={{ border: "1px solid" }}
        >
          <Grid item xs>
            <CustomizedTooltip title="Based on the selection in Web-Suite Unserviceable Component Section - Remove Reason Selection.">
              <Typography gutterBottom variant="h6" component="h2" align="center">
                Reported Fail-On-Fit: {chartData?.allFailOnFitCount}
              </Typography>
            </CustomizedTooltip>
            <CustomizedTooltip title="Fault found units during shop visit excluding CID and MISUSE.">
              <Typography gutterBottom variant="h6" component="h2" align="center">
                Confirmed Fail-On-Fit: {chartData?.confirmedFailOnFitCount}
              </Typography>
            </CustomizedTooltip>
          </Grid>
          <Grid item xs>
            <CustomizedTooltip title="Total number of realized requisitions. (Excluded: Mock/Initial, Return Unused, Cancelled, Rejected)">
              <Typography gutterBottom variant="h6" component="h2" align="center">
                Total Qty: {chartData?.totalRequestCount}
              </Typography>
            </CustomizedTooltip>
            <CustomizedTooltip title="Confirmed Fail-on-Fit Qty / Total Qty">
              <Typography gutterBottom variant="h6" component="h2" align="center">
                Rate: % {chartData?.confirmedFofByTotalRequestRatio}
              </Typography>
            </CustomizedTooltip>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default FailOnFitRateSource;
